import type { MeResponse } from "@zeiterfassung/shared";
import { Bell, LogOut, Menu } from "lucide-react";
import { createContext, type ReactNode, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, Outlet, useLocation, useNavigate } from "react-router";
import { useLogout } from "@/api/queries/me";
import { TitleInTopBarContext } from "@/components/ui/PageHeader";
import { Sheet } from "@/components/ui/Sheet";
import { routes } from "@/lib/routes";
import { OfflineBanner } from "@/pwa/OfflineBanner";
import { PwaUpdateGate } from "@/pwa/PwaUpdateGate";
import { useOnline } from "@/pwa/useOnline";
import { BottomNav } from "./BottomNav";
import { Sidebar, SidebarLinks, SidebarProfileLink, useSidebarItems } from "./Sidebar";
import { TopBar } from "./TopBar";

// Page title for the mobile top bar; pages set it through `usePageTitle`.
const TitleContext = createContext<{ title: string; setTitle: (title: string) => void }>({
  title: "",
  setTitle: () => {},
});

export function usePageTitle(title: string) {
  const { setTitle } = useContext(TitleContext);
  useEffect(() => {
    setTitle(title);
    document.title = `${title} · Zeiterfassung`;
  }, [title, setTitle]);
}

export const MeContext = createContext<MeResponse | null>(null);

// The logged-in user; only rendered below a role guard, so never null there.
export function useCurrentUser(): MeResponse {
  const me = useContext(MeContext);
  if (!me) throw new Error("useCurrentUser outside of AppShell");
  return me;
}

// Dispatcher pages stay network-only: just say that the connection is gone (B2).
function OfflineBar() {
  const { t } = useTranslation("translation", { keyPrefix: "pwa" });
  const online = useOnline();
  if (online) return null;
  return (
    <div
      role="status"
      className="bg-warning-soft px-4 py-2 text-center text-sm text-warning-strong"
    >
      {t("offline.noConnection")}
    </div>
  );
}

// Employee shell: top bar + content (max 640px) + bottom tabs.
export function EmployeeShell({ me }: { me: MeResponse }) {
  const [title, setTitle] = useState("");
  return (
    <MeContext.Provider value={me}>
      <TitleContext.Provider value={{ title, setTitle }}>
        <TitleInTopBarContext.Provider value="always">
          <div className="flex min-h-dvh flex-col pb-20">
            <PwaUpdateGate />
            <TopBar title={title} unread={me.unreadNotifications} />
            <OfflineBanner userId={me.user.id} />
            <main className="mx-auto w-full max-w-2xl flex-1 px-4 py-4">
              <Outlet />
            </main>
            <BottomNav />
          </div>
        </TitleInTopBarContext.Provider>
      </TitleContext.Provider>
    </MeContext.Provider>
  );
}

// Dispatcher/admin shell: sidebar on desktop, top bar with a menu sheet on phones.
export function DispatcherShell({ me }: { me: MeResponse }) {
  const { t } = useTranslation("translation", { keyPrefix: "common" });
  const [title, setTitle] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();
  const logout = useLogout();
  const location = useLocation();
  const role = me.user.role === "admin" ? "admin" : "dispatcher";
  const { main, admin } = useSidebarItems(role);
  // biome-ignore lint/correctness/useExhaustiveDependencies: closes the phone menu on navigation
  useEffect(() => setMenuOpen(false), [location.pathname]);
  const onLogout = () => logout.mutate(undefined, { onSuccess: () => navigate(routes.login) });
  return (
    <MeContext.Provider value={me}>
      <TitleContext.Provider value={{ title, setTitle }}>
        <TitleInTopBarContext.Provider value="mobile">
          <div className="flex min-h-dvh">
            <PwaUpdateGate />
            <Sidebar role={role} userName={me.user.name} onLogout={onLogout} />
            <div className="flex min-w-0 flex-1 flex-col">
              <TopBar
                className="md:hidden"
                title={title}
                unread={me.unreadNotifications}
                leading={
                  <button
                    type="button"
                    aria-label={t("menu")}
                    onClick={() => setMenuOpen(true)}
                    className="-ml-2 flex size-11 items-center justify-center rounded-md text-zinc-700 hover:bg-zinc-100"
                  >
                    <Menu className="size-6" aria-hidden />
                  </button>
                }
              />
              <header className="hidden h-14 items-center justify-end gap-2 border-zinc-200 border-b bg-white px-6 md:flex">
                <Link
                  to={routes.notifications}
                  className="relative flex size-11 items-center justify-center rounded-md text-zinc-700 hover:bg-zinc-100"
                  aria-label={
                    me.unreadNotifications > 0
                      ? t("notificationsUnread", { count: me.unreadNotifications })
                      : t("notifications")
                  }
                >
                  <Bell className="size-5" aria-hidden />
                  {me.unreadNotifications > 0 ? (
                    <span className="absolute top-1.5 right-1.5 min-w-4 rounded-full bg-danger px-1 text-center font-semibold text-[10px] text-white leading-4">
                      {me.unreadNotifications > 9 ? "9+" : me.unreadNotifications}
                    </span>
                  ) : null}
                </Link>
              </header>
              <OfflineBar />
              <main className="mx-auto w-full max-w-6xl flex-1 px-4 py-4 md:px-6 md:py-6">
                <Outlet key={location.pathname} />
              </main>
            </div>
          </div>
          <Sheet open={menuOpen} onOpenChange={setMenuOpen} title={t("appName")} side="left">
            <nav aria-label={t("nav.main")} className="flex flex-col gap-4">
              <SidebarLinks items={main} onNavigate={() => setMenuOpen(false)} />
              {admin.length > 0 ? (
                <div>
                  <p className="px-3 pb-1 font-semibold text-xs text-zinc-500 uppercase tracking-wide">
                    {t("nav.admin")}
                  </p>
                  <SidebarLinks items={admin} onNavigate={() => setMenuOpen(false)} />
                </div>
              ) : null}
              <SidebarProfileLink userName={me.user.name} onNavigate={() => setMenuOpen(false)} />
              <button
                type="button"
                onClick={onLogout}
                className="flex min-h-11 items-center gap-3 rounded-md px-3 text-sm text-zinc-700 hover:bg-zinc-100"
              >
                <LogOut className="size-5" aria-hidden />
                {t("logout")}
              </button>
            </nav>
          </Sheet>
        </TitleInTopBarContext.Provider>
      </TitleContext.Provider>
    </MeContext.Provider>
  );
}

export function PageContent({ children }: { children: ReactNode }) {
  return <div className="flex flex-col gap-4">{children}</div>;
}
